import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Music, Drama, Palette, Feather, Flower2, Clock, Download, FileText } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import Reveal from "../components/Reveal";
import { heroSlides, stats, projects, archiveEvents, arteNaturaFiori } from "../mock/mock";

const disciplines = [
  { icon: Feather, tKey: "discipline1" },
  { icon: Music, tKey: "discipline2" },
  { icon: Drama, tKey: "discipline3" },
  { icon: Palette, tKey: "discipline4" },
];

const Home = () => {
  const { t, pick } = useLang();
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[620px] w-full overflow-hidden">
        {heroSlides.map((s, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${
              i === slide ? "opacity-100" : "opacity-0"
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${s.image})`,
                transform: i === slide ? "scale(1.06)" : "scale(1)",
                transition: "transform 6000ms ease-out",
              }}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/75" />

        <div className="relative h-full mx-auto max-w-7xl px-5 lg:px-8 flex flex-col justify-center">
          <span className="text-[#f0c3ab] text-xs md:text-sm uppercase tracking-[0.3em] mb-6 animate-[fadeIn_1s_ease]">
            {t("home.kicker")}
          </span>
          <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] max-w-4xl">
            {t("home.title")}
          </h1>
          <p className="mt-6 text-white/80 text-sm md:text-base uppercase tracking-[0.22em]">
            {t("home.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/interdanza"
              className="group inline-flex items-center gap-2 bg-[#b5533a] hover:bg-[#a0472f] text-white px-7 py-3.5 text-sm uppercase tracking-[0.14em] transition-colors"
            >
              {t("home.ctaProjects")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/chi-siamo"
              className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white hover:text-[#1c1b1a] px-7 py-3.5 text-sm uppercase tracking-[0.14em] transition-colors"
            >
              {t("home.ctaAbout")}
            </Link>
          </div>

          {/* slide dots */}
          <div className="absolute bottom-10 left-5 lg:left-8 flex gap-2.5">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`slide ${i + 1}`}
                className={`h-[3px] transition-all duration-500 ${
                  i === slide ? "w-10 bg-[#e08a5f]" : "w-5 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* NOVITÀ — FIORI (3ª edizione) */}
      <section className="bg-[#15211a] py-16 lg:py-20 text-[#e9e4d8]" data-testid="home-fiori-novita">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src={arteNaturaFiori.heroImage}
                alt="Fiori"
                className="w-full aspect-[16/11] object-cover object-center"
                data-testid="home-fiori-image"
              />
              <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#e3a9b6] text-[#15211a] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-medium">
                <Flower2 className="w-3.5 h-3.5" /> {t("home.novitaLabel")}
              </span>
            </div>

            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-[#e3a9b6] text-xs uppercase tracking-[0.2em]">
                {pick(arteNaturaFiori.edition)}
              </span>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-white leading-tight">
                {arteNaturaFiori.title}
                <span className="block font-serif italic text-2xl md:text-3xl text-[#e3a9b6] mt-1">
                  {pick(arteNaturaFiori.subtitle)}
                </span>
              </h2>
              <p className="mt-3 text-sm text-[#9db89f]" data-testid="home-fiori-partof">
                {pick(arteNaturaFiori.partOf)}
              </p>
              <p className="mt-5 text-[#cbc5b8] leading-relaxed">{pick(arteNaturaFiori.intro2)}</p>

              {/* highlights */}
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#e3a9b6]">
                {t("home.fioriInProject")}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {arteNaturaFiori.disciplines.map((d, i) => (
                  <span
                    key={i}
                    className="border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-[#d8d2c6]"
                  >
                    {pick(d.title)}
                  </span>
                ))}
                <span className="border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-[#d8d2c6]">
                  {pick(arteNaturaFiori.contest.title)}
                </span>
              </div>

              {/* contest coming soon */}
              <div className="mt-6 flex items-start gap-3 border border-[#e3a9b6]/30 bg-[#e3a9b6]/10 px-4 py-3">
                <Clock className="w-5 h-5 shrink-0 text-[#e3a9b6] mt-0.5" />
                <span className="text-sm text-[#efe6e0]" data-testid="home-fiori-contest-soon">
                  {t("home.fioriContestSoon")}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
              <Link
                to="/regolamento-racconta-un-fiore"
                data-testid="home-fiori-rules-page-link"
                className="inline-flex items-center gap-2 text-sm text-[#e3a9b6] hover:text-white underline underline-offset-4 transition-colors"
              >
                <FileText className="w-4 h-4" /> {t("home.fioriRulesRead")}
              </Link>
              <a
                href={arteNaturaFiori.contest.rulesUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="home-fiori-rules-download"
                className="inline-flex items-center gap-2 text-sm text-[#e3a9b6] hover:text-white underline underline-offset-4 transition-colors"
              >
                <Download className="w-4 h-4" /> {t("home.fioriRulesDownload")}
              </a>
              </div>

              <Link
                to="/arte-e-natura"
                data-testid="home-fiori-cta"
                className="mt-7 group inline-flex items-center gap-2 bg-[#e3a9b6] hover:bg-[#d693a3] text-[#15211a] px-7 py-3.5 text-sm uppercase tracking-[0.14em] font-medium transition-colors"
              >
                {t("home.fioriCta")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LEAD + STATS */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 text-center">
          <Reveal>
            <p className="font-serif text-2xl md:text-3xl lg:text-[2.2rem] leading-[1.4] text-[#2b2926]">
              {t("home.lead")}
            </p>
          </Reveal>
        </div>
        <div className="mx-auto max-w-6xl px-5 lg:px-8 mt-16 grid grid-cols-2 lg:grid-cols-4 gap-y-10">
          {stats.map((s, i) => (
            <Reveal key={s.key} delay={i * 100} className="text-center">
              <div className="font-serif text-4xl md:text-5xl text-[#b5533a]">{s.value}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.16em] text-[#8a8478]">
                {t(`home.${s.key}`)}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <span className="text-[#b5533a] text-xs uppercase tracking-[0.24em]">
              {t("common.since")}
            </span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("home.whatTitle")}
            </h2>
            <p className="mt-4 text-[#6a655c] leading-relaxed">{t("home.whatLead")}</p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {disciplines.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal
                  key={d.tKey}
                  delay={i * 90}
                  className="group border border-[#eae4d9] hover:border-[#b5533a] p-8 transition-colors bg-[#faf7f2] hover:-translate-y-1"
                >
                  <Icon className="w-8 h-8 text-[#b5533a]" strokeWidth={1.4} />
                  <h3 className="mt-5 font-serif text-2xl text-[#1c1b1a]">
                    {t(`home.${d.tKey}`)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#6a655c]">
                    {t(`home.${d.tKey}d`)}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("home.projectsTitle")}
            </h2>
            <p className="mt-4 text-[#6a655c]">{t("home.projectsLead")}</p>
          </Reveal>

          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={i * 120}>
                <Link to={p.slug} className="group block relative overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-8">
                    <span className="text-[#f0c3ab] text-xs uppercase tracking-[0.2em]">
                      {pick(p.tag)}
                    </span>
                    <h3 className="mt-2 font-serif text-3xl md:text-4xl text-white">{p.name}</h3>
                    <p className="mt-2 text-white/80 text-sm max-w-md">{pick(p.excerpt)}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-white text-sm uppercase tracking-[0.14em]">
                      {t("common.discover")}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="relative bg-[#1c1b1a] py-24 lg:py-32 overflow-hidden">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center relative">
          <Reveal>
            <span className="text-[#e08a5f] text-xs uppercase tracking-[0.28em]">
              arts4all
            </span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-white">
              {t("home.missionTitle")}
            </h2>
            <p className="mt-8 text-[#c7c0b4] text-lg leading-relaxed">
              {t("home.missionText")}
            </p>
            <Link
              to="/contatti"
              className="mt-10 inline-flex items-center gap-2 bg-[#b5533a] hover:bg-[#a0472f] text-white px-7 py-3.5 text-sm uppercase tracking-[0.14em] transition-colors"
            >
              {t("common.support")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NEWS PREVIEW */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="flex items-end justify-between gap-4 mb-12">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("home.newsTitle")}
            </h2>
            <Link
              to="/archivio"
              className="hidden sm:inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-[#b5533a] hover:gap-3 transition-all"
            >
              {t("nav.archive")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-3">
            {archiveEvents.map((e, i) => (
              <Reveal key={i} delay={i * 100} className="group">
                <div className="aspect-[16/10] overflow-hidden mb-5">
                  <img
                    src={e.image}
                    alt={pick(e.title)}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                </div>
                <span className="text-[#b5533a] text-xs uppercase tracking-[0.2em]">{e.year}</span>
                <h3 className="mt-2 font-serif text-2xl text-[#1c1b1a] leading-snug">
                  {pick(e.title)}
                </h3>
                <p className="mt-1 text-sm text-[#8a8478]">{pick(e.place)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
